import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import './NotificationDropdown.scss';

const NotificationDropdown = ({ isOpen, onClose }) => {
    const dropdownRef = useRef(null);
    const { notifications, loading, fetchNotifications, markAsRead } = useNotifications();

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]); // Remove fetchNotifications from dependency array to prevent infinite loop

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const getNotificationIcon = (content) => {
        if (content.toLowerCase().includes('essay')) return '📝';
        if (content.toLowerCase().includes('quiz')) return '🏅';
        if (content.toLowerCase().includes('grade')) return '📊';
        if (content.toLowerCase().includes('feedback')) return '💬';
        if (content.toLowerCase().includes('teacher')) return '👨‍🏫';
        if (content.toLowerCase().includes('assignment')) return '📋';
        return '🔔';
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInHours < 48) return 'Yesterday';
        return date.toLocaleDateString();
    };

    // Get recent notifications (max 5) for dropdown
    const recentNotifications = notifications.slice(0, 5);

    if (!isOpen) return null;

    return (
        <div className="notification-dropdown-overlay">
            <div className="notification-dropdown" ref={dropdownRef}>
                <div className="notification-dropdown-header">
                    <h3>Notifications</h3>
                    {notifications.length > 0 && (
                        <button 
                            className="view-all-btn"
                            onClick={() => {
                                window.location.href = '/notifications';
                                onClose();
                            }}
                        >
                            View All
                        </button>
                    )}
                </div>

                <div className="notification-dropdown-content">
                    {loading ? (
                        <div className="dropdown-loading">
                            <div className="loading-spinner"></div>
                            <span>Loading...</span>
                        </div>
                    ) : recentNotifications.length === 0 ? (
                        <div className="dropdown-empty">
                            <div className="empty-icon">🔔</div>
                            <p>You have no notifications</p>
                        </div>
                    ) : (
                        <div className="notification-dropdown-list">
                            {recentNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`dropdown-notification-item ${!notification.is_read ? 'unread' : 'read'}`}
                                    onClick={() => {
                                        if (!notification.is_read) {
                                            markAsRead(notification.id);
                                        }
                                        if (notification.link) {
                                            window.location.href = notification.link;
                                            onClose();
                                        }
                                    }}
                                >
                                    <div className="dropdown-notification-icon">
                                        {getNotificationIcon(notification.content)}
                                    </div>
                                    <div className="dropdown-notification-content">
                                        <div className="dropdown-notification-message">
                                            {notification.content}
                                        </div>
                                        <div className="dropdown-notification-time">
                                            {formatTimeAgo(notification.created_at)}
                                        </div>
                                    </div>
                                    {!notification.is_read && (
                                        <div className="dropdown-unread-indicator"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {notifications.length > 5 && (
                    <div className="notification-dropdown-footer">
                        <button 
                            className="view-more-btn"
                            onClick={() => {
                                window.location.href = '/notifications';
                                onClose();
                            }}
                        >
                            View {notifications.length - 5} more notifications
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationDropdown; 